/**
 * Curated IATA code → pt-BR city display name (accented). Hand-maintained, not generated:
 * the pt-BR names (e.g. "Londres", "Nova York") are intentional — they are NOT derivable from
 * the English dataset behind the generated world map (`airport_cities_world.ts`).
 *
 * This is the OVERRIDE tier of `cityByAirport`: entries here win over the generated world
 * map, which covers every other airport in dataset spelling (unaccented, raw casing). Since
 * the planner's DE/PARA combobox labels each airport `IATA + cidade`, and the Live Activity
 * card, the landed greeting and the localized notifications name the cities of every flight,
 * this text is READ by the traveler — so an entry earns its keep when the dataset spelling
 * is wrong for a pt-BR reader, or missing, in one of four ways:
 *
 *  1. English/foreign exonym where pt-BR has its own name (`London` → `Londres`,
 *     `Venezia` → `Veneza`);
 *  2. missing accent or wrong casing (`Belem` → `Belém`, `Foz Do Iguacu` → `Foz do Iguaçu`);
 *  3. the dataset names the MUNICIPALITY, not the city served (`Arnavutkoy` → `Istambul`,
 *     `Tocumen` → `Cidade do Panamá`, `Dulles` → `Washington`), or strings several places
 *     together (`Bordeaux/Merignac` → `Bordeaux`);
 *  4. the dataset has no entry under the IATA code at all (`SMT`; `RRJ` is there by ICAO
 *     alone), so `cityByAirport` answers `null` and the card shows no city. The name follows
 *     rule 3 — the city served, not the municipality, neighborhood or district the voucher
 *     label names (`CORREIA PINTO` → `Lages`). The generated time zone table lacks it too, so
 *     it also takes an `AIRPORT_TIMEZONE_OVERRIDES` entry: without a zone,
 *     `isBrazilianAirport` misses it and the `en`/`es` name degrades to the code.
 *
 * Two rules keep this from drifting into a translation of the whole world (~7k airports):
 * only airports a Brazilian corporate traveler actually flies (the emitted flight vouchers
 * are the evidence), and do not ADD an entry that merely restates the dataset (`Barcelona`,
 * `Santiago`, `Lima`) — it is maintenance with no effect. A handful of such no-ops predate
 * this tier's reading role, from when it was the sibling of the destination-photo catalog;
 * they are harmless, not a precedent.
 *
 * Airports of one metro must agree, because the offers filter groups by this exact string
 * (`airportOptions`): JFK spelled one way and LGA another split New York into two groups.
 *
 * Changing an existing value is not free — the trip destination-image cache is keyed by a
 * slug of this name, so a rename orphans the cached row and re-queries the provider (the
 * slug folds accents and case, so a type-2 fix keeps the key).
 *
 * An exonym (type 1) leaves the planner's exact-city match alone: the flight agent's ranker
 * (`pickAirportRef`) compares the spoken term with every name of the airport's city
 * (`cityNamesByAirport`: this one, the dataset's, en, es), so after `Hamburg` → `Hamburgo`
 * the term "Hamburg" still matches HAM.
 */
export const AIRPORT_CITIES: Record<string, string> = {
  // Brasil
  GRU: 'São Paulo',
  CGH: 'São Paulo',
  VCP: 'Campinas',
  SDU: 'Rio de Janeiro',
  GIG: 'Rio de Janeiro',
  BSB: 'Brasília',
  CNF: 'Belo Horizonte',
  PLU: 'Belo Horizonte',
  CWB: 'Curitiba',
  POA: 'Porto Alegre',
  FLN: 'Florianópolis',
  SSA: 'Salvador',
  REC: 'Recife',
  FOR: 'Fortaleza',
  NAT: 'Natal',
  BEL: 'Belém',
  VIX: 'Vitória',
  GYN: 'Goiânia',
  CGB: 'Cuiabá',
  SLZ: 'São Luís',
  MCZ: 'Maceió',
  JPA: 'João Pessoa',
  MCP: 'Macapá',
  LDB: 'Londrina',
  RAO: 'Ribeirão Preto',
  IGU: 'Foz do Iguaçu',

  // Regionais com viagem corporativa: o dataset erra acento e title-caseia as conectivas
  // ("Caxias Do Sul", "Sao Jose Dos Campos"). O card de voo da Live Activity (D26) e a
  // saudação do pouso (D18) mostram a cidade o voo inteiro, então entra todo regional com
  // voo regular que viajante corporativo voa (a base de vouchers emitidos é a evidência).
  // Os que ninguém voa ficam de fora — cada entrada é manutenção, e o combobox resolve
  // pelo tier gerado.
  UDI: 'Uberlândia',
  // GMS acompanha UDI porque o DATASET já o mapeia em "Uberlandia" — a associação
  // não nasce aqui, este override só acrescenta o acento e mantém o metro coerente
  // (o filtro de ofertas agrupa pela string, e divergir parte a cidade em dois).
  // Se a associação do dataset estiver errada (há fonte dizendo que GMS é Guimarães,
  // MA), o conserto é no gerado/upstream: tirar o override devolveria "Uberlandia"
  // sem acento, mesma cidade, e ainda re-partiria o grupo.
  GMS: 'Uberlândia',
  SJP: 'São José do Rio Preto',
  SJK: 'São José dos Campos',
  CXJ: 'Caxias do Sul',
  MGF: 'Maringá',
  XAP: 'Chapecó',
  JDO: 'Juazeiro do Norte',
  STM: 'Santarém',
  MAB: 'Marabá',
  IOS: 'Ilhéus',
  AAX: 'Araxá',
  ARU: 'Araçatuba',
  AUX: 'Araguaína',
  BPG: 'Barra do Garças',
  CKS: 'Carajás',
  CMG: 'Corumbá',
  CZS: 'Cruzeiro do Sul',
  DIQ: 'Divinópolis',
  FEN: 'Fernando de Noronha',
  GEL: 'Santo Ângelo',
  IZA: 'Juiz de Fora',
  // JDF (o aeroporto antigo da cidade) acompanha IZA pela regra de metro.
  JDF: 'Juiz de Fora',
  JPR: 'Ji-Paraná',
  MII: 'Marília',
  MVF: 'Mossoró',
  OIA: 'Ourilândia do Norte',
  PHB: 'Parnaíba',
  PMG: 'Ponta Porã',
  POJ: 'Patos de Minas',
  ROO: 'Rondonópolis',
  TFF: 'Tefé',
  TFL: 'Teófilo Otoni',
  TJL: 'Três Lagoas',
  TUR: 'Tucuruí',
  // O dataset grava o MUNICÍPIO (Oriximiná); TMT serve Porto Trombetas, a vila da
  // mineração — o aeroporto da sede do município é o ORX.
  TMT: 'Porto Trombetas',

  // Ausentes do dataset (motivo 4): sem a entrada, o código cru dá null e o card fica sem
  // cidade. Cada um tem também o fuso em `AIRPORT_TIMEZONE_OVERRIDES`. O rótulo do voucher
  // diz onde o aeroporto fica; aqui vai a cidade que ele serve.
  SMT: 'Sorriso',
  // "CORREIA PINTO" é o município do aeroporto do Planalto Serrano, a 28 km de Lages, a
  // cidade que ele serve. O aeroporto da cidade (LAJ, "Lages" no dataset) não teve voucher
  // emitido em 12 meses; a grafia igual mantém os dois na mesma cidade.
  EEA: 'Lages',
  // "JACAREPAGUA" é o bairro: RRJ fica no Rio e segue a regra de metro (GIG, SDU, RIO).
  RRJ: 'Rio de Janeiro',
  LHN: 'Linhares',
  // Patos (PB). O nome vira cidade conhecida do ranker (I2): "Patos" com só Patos de Minas
  // (POJ) na lista pergunta, como "Porto" com Porto Alegre.
  JPO: 'Patos',
  PYT: 'Paracatu',
  // "SANTO AMARO DE MINAS" é o distrito do aeroporto Elias Breder, que serve Manhuaçu.
  JMA: 'Manhuaçu',

  // América do Sul e Central
  EZE: 'Buenos Aires',
  AEP: 'Buenos Aires',
  MVD: 'Montevidéu',
  ASU: 'Assunção',
  LPB: 'La Paz',
  BOG: 'Bogotá',
  MDE: 'Medellín',
  // EOH junto com MDE por causa da regra de metro: sem ele o aeroporto central de
  // Medellín cairia no `Medellin` do dataset e o filtro de ofertas partiria a
  // cidade em dois grupos — exatamente o que trazer MDE pra cá foi evitar.
  EOH: 'Medellín',
  PTY: 'Cidade do Panamá',
  IBE: 'Ibagué',
  COR: 'Córdoba',
  // O dataset grava "San Jose" também para San Jose, na Califórnia (SJC): o acento é o
  // que separa as duas cidades. SYQ, o outro aeroporto de San José, acompanha o SJO.
  SJO: 'San José',
  SYQ: 'San José',
  // Onde o dataset junta lugares ("Cayenne / Rochambeau") ou grava o município do
  // aeroporto ("Santa Clara", "Zandery", "La Mesa"), a cidade é a que o voo serve.
  CAY: 'Caiena',
  LSC: 'La Serena',
  PBM: 'Paramaribo',
  SAL: 'San Salvador',
  SAP: 'San Pedro Sula',

  // América do Norte
  JFK: 'Nova York',
  LGA: 'Nova York',
  EWR: 'Newark',
  MIA: 'Miami',
  IAD: 'Washington',
  // O dataset grava DFW como "Dallas-Fort Worth" e DAL como "Dallas": o ranker de
  // candidatos via só DAL como a cidade exata e escolhia Love Field em silêncio para
  // quem disse "Dallas" (#4157). A regra de metro acima vale sem código metro também.
  DFW: 'Dallas',
  // Exônimo pt-BR: sem ele, "Filadélfia" casa só FLM (Filadelfia, Paraguai).
  PHL: 'Filadélfia',
  MEX: 'Cidade do México',
  CUN: 'Cancún',
  MID: 'Mérida',
  // Mesma regra dos lugares juntos / do município: "Raleigh/Durham", "Hebron" (CVG fica
  // em Hebron, Kentucky) e "Silao" (o BJX fica em Silao e serve León).
  RDU: 'Raleigh',
  CVG: 'Cincinnati',
  BJX: 'León',

  // Europa
  LIS: 'Lisboa',
  MAD: 'Madri',
  LHR: 'Londres',
  LGW: 'Londres',
  // O dataset grava LCY, STN e LTN como "London": a regra de metro os traz junto com
  // LHR (STN e LTN mesmo sem voucher emitido), senão Londres partia em dois grupos e
  // eles saíam do LON (`airportMatchesCode`). SEN e BGY, que o IATA também põe no LON
  // e no MIL, ficam de fora: o dataset os grava como cidades próprias (Southend,
  // Bergamo), e nenhum voucher emitido desde out/2023 passa por eles.
  LCY: 'Londres',
  STN: 'Londres',
  LTN: 'Londres',
  CDG: 'Paris',
  FCO: 'Roma',
  MXP: 'Milão',
  // LIN acompanha MXP pela mesma regra: o dataset grava "Milan".
  LIN: 'Milão',
  VCE: 'Veneza',
  AMS: 'Amsterdã',
  BRU: 'Bruxelas',
  FRA: 'Frankfurt',
  MUC: 'Munique',
  BER: 'Berlim',
  ZRH: 'Zurique',
  GVA: 'Genebra',
  VIE: 'Viena',
  CPH: 'Copenhague',
  ARN: 'Estocolmo',
  IST: 'Istambul',
  // O dataset junta lugares: "Bordeaux/Merignac", "Bale/Mulhouse". BSL e MLH são os dois
  // códigos do mesmo EuroAirport, então concordam.
  BOD: 'Bordeaux',
  BSL: 'Basileia',
  MLH: 'Basileia',

  // África, Oriente Médio e Ásia
  JNB: 'Joanesburgo',
  NRT: 'Tóquio',
  HND: 'Tóquio',
  ICN: 'Seul',
  // GMP acompanha ICN pela regra de metro: o dataset grava "Seoul" (#4157).
  GMP: 'Seul',
  PEK: 'Pequim',
  PVG: 'Xangai',
  SIN: 'Singapura',
};
